/* ============================================================================
 * CHANNEL.EXE — live channels & social syndication
 * ----------------------------------------------------------------------------
 * The engagement layer: livestream modules and post syndication routed through
 * decentralised feeds (Warpcast / Farcaster).
 *
 * HONEST STATUS:
 *   • Publishing to a Farcaster hub requires a signed Farcaster message (an
 *     Ed25519 signer registered on-chain for your FID) and a hub endpoint.
 *     Neither exists in this OS, so the composer builds and shows the payload
 *     it WOULD submit, and says it is not broadcast.
 *   • Reads are attempted against VITE_FARCASTER_HUB when configured. Unset, the
 *     feed reports "not configured" rather than showing invented posts.
 *   • Livestream embedding needs a provider (Livepeer / Mux). Not wired.
 * ========================================================================= */

import { useCallback, useMemo, useState } from 'react';
import {
  ROLES,
  hasPermission,
  type PermissionKey,
} from '../lib/roles';

const HUB = import.meta.env.VITE_FARCASTER_HUB;

interface LogLine {
  text: string;
  kind: 'info' | 'ok' | 'err';
}

/** A Farcaster cast-shaped payload. Shown, never submitted. */
interface CastDraft {
  text: string;
  embeds: string[];
  channel: string;
  timestamp: number;
  /** Farcaster messages are Ed25519-signed; we have no signer. */
  signer: null;
}

const CHANNELS: { id: string; label: string; blurb: string; requires: PermissionKey }[] =
  [
    {
      id: 'supercompute',
      label: '/supercompute',
      blurb: 'The org channel. Org-voice broadcasts.',
      requires: 'BROADCAST_ORG',
    },
    {
      id: 'founders',
      label: '/founders',
      blurb: 'Founding-member channel.',
      requires: 'BROADCAST_OWN',
    },
    {
      id: 'school',
      label: '/web3-school',
      blurb: 'Course announcements and cohort events.',
      requires: 'SCHOOL_AUTHOR',
    },
    {
      id: 'presence',
      label: '/presence',
      blurb: 'Event check-in feeds and capsule drops.',
      requires: 'EVENT_CREATE',
    },
  ];

export function Channel() {
  const [text, setText] = useState('');
  const [embed, setEmbed] = useState('');
  const [channel, setChannel] = useState(CHANNELS[0].id);
  const [roles, setRoles] = useState<string[]>([]);
  const [log, setLog] = useState<LogLine[]>([]);

  const push = useCallback((kind: LogLine['kind'], t: string) => {
    setLog((prev) => [...prev.slice(-40), { text: t, kind }]);
  }, []);

  const active = useMemo(
    () => CHANNELS.find((c) => c.id === channel) ?? CHANNELS[0],
    [channel],
  );

  const permitted =
    roles.length > 0 && hasPermission(roles, active.requires);

  const draft: CastDraft = useMemo(
    () => ({
      text,
      embeds: embed.trim() ? [embed.trim()] : [],
      channel: active.label,
      timestamp: Math.floor(Date.now() / 1000),
      signer: null,
    }),
    [text, embed, active],
  );

  const attemptBroadcast = useCallback(() => {
    if (!permitted) {
      push('err', `Blocked — ${active.requires} not held.`);
      return;
    }
    if (!text.trim()) {
      push('err', 'Abort: empty cast.');
      return;
    }
    push('err', 'NOT BROADCAST — no Farcaster signer registered for this session.');
    if (!HUB) {
      push('err', 'No hub configured (VITE_FARCASTER_HUB unset) — cannot even submit.');
    }
  }, [permitted, active, text, push]);

  const probeHub = useCallback(async () => {
    if (!HUB) {
      push('err', 'No hub configured (VITE_FARCASTER_HUB unset).');
      return;
    }
    try {
      const res = await fetch(`${HUB}/v1/info`);
      const body: unknown = await res.json().catch(() => null);
      push(res.ok ? 'ok' : 'err', `hub ${res.status} ${JSON.stringify(body)}`);
    } catch (e) {
      push('err', e instanceof Error ? e.message : String(e));
    }
  }, [push]);

  return (
    <div className="stack">
      <div className="panel">
        <div className="row">
          <span className="row__k">Feed protocol</span>
          <span className="row__v">Farcaster (Warpcast)</span>
        </div>
        <div className="row">
          <span className="row__k">Hub</span>
          <span className="row__v">
            {HUB ?? <span className="tag tag--warn">not configured</span>}
          </span>
        </div>
        <div className="row">
          <span className="row__k">Signer</span>
          <span className="row__v">
            <span className="tag tag--warn">none — Ed25519 signer not registered</span>
          </span>
        </div>
        <div className="row">
          <span className="row__k">Livestream</span>
          <span className="row__v">
            <span className="tag tag--warn">no provider (Livepeer / Mux) wired</span>
          </span>
        </div>
      </div>

      <hr className="rule" />

      <span className="eyebrow">session roles — broadcast gate</span>
      <div className="hstack">
        {ROLES.filter((r) =>
          [
            'marketing',
            'community',
            'member',
            'channel',
            'partner',
            'student',
          ].includes(r.id),
        ).map((r) => (
          <button
            key={r.id}
            type="button"
            className="btn"
            style={
              roles.includes(r.id)
                ? { borderColor: 'var(--hud-yellow)', color: 'var(--hud-yellow)' }
                : undefined
            }
            onClick={() =>
              setRoles((prev) =>
                prev.includes(r.id)
                  ? prev.filter((x) => x !== r.id)
                  : [...prev, r.id],
              )
            }
          >
            {r.label}
          </button>
        ))}
      </div>

      <hr className="rule" />

      <div className="hstack">
        {CHANNELS.map((c) => (
          <button
            key={c.id}
            type="button"
            className="btn"
            style={
              channel === c.id
                ? { borderColor: 'var(--hud-yellow)', color: 'var(--hud-yellow)' }
                : undefined
            }
            onClick={() => setChannel(c.id)}
            title={`${c.blurb} (requires ${c.requires})`}
          >
            {c.label}
          </button>
        ))}
      </div>
      <p className="note">
        {active.blurb} · requires <strong>{active.requires}</strong>{' '}
        {roles.length > 0 &&
          (permitted ? (
            <span className="tag tag--ok">PASS</span>
          ) : (
            <span className="tag tag--err">BLOCKED</span>
          ))}
      </p>

      <div className="stack" style={{ gap: 4 }}>
        <label className="label" htmlFor="ch-text">Cast</label>
        <textarea
          id="ch-text"
          rows={4}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What is the bubble doing?"
          style={{
            background: 'var(--site-bg)',
            border: '1px solid var(--wos-border-dim)',
            padding: '4px 6px',
            resize: 'vertical',
          }}
        />
        <label className="label" htmlFor="ch-embed">Embed URL (optional)</label>
        <input
          id="ch-embed"
          value={embed}
          onChange={(e) => setEmbed(e.target.value)}
          placeholder="https://…"
          style={{
            background: 'var(--site-bg)',
            border: '1px solid var(--wos-border-dim)',
            padding: '4px 6px',
          }}
        />
      </div>

      <div className="hstack">
        <button type="button" className="btn btn--primary" onClick={attemptBroadcast}>
          Broadcast
        </button>
        <button type="button" className="btn" onClick={probeHub}>
          Probe hub
        </button>
      </div>

      <span className="eyebrow">cast payload (staged, unsigned)</span>
      <div className="panel">
        <div className="row">
          <span className="row__k">channel</span>
          <span className="row__v">{draft.channel}</span>
        </div>
        <div className="row">
          <span className="row__k">text len</span>
          <span className="row__v">
            {draft.text.length} / 320{' '}
            {draft.text.length > 320 && (
              <span className="tag tag--err">OVER</span>
            )}
          </span>
        </div>
        <div className="row">
          <span className="row__k">embeds</span>
          <span className="row__v">{draft.embeds.length}</span>
        </div>
        <div className="row">
          <span className="row__k">signature</span>
          <span className="row__v">
            <span className="tag tag--warn">null — no Ed25519 signer</span>
          </span>
        </div>
        <div className="row">
          <span className="row__k">timestamp</span>
          <span className="row__v">{draft.timestamp}</span>
        </div>
      </div>

      <div className="log">
        {log.length === 0 && (
          <div className="log__line log__line--dim">
            ready. a cast needs a registered Farcaster signer before it can leave
            this machine.
          </div>
        )}
        {log.map((l, i) => (
          <div
            key={i}
            className={
              'log__line' +
              (l.kind === 'err'
                ? ' log__line--err'
                : l.kind === 'ok'
                  ? ' log__line--ok'
                  : ' log__line--dim')
            }
          >
            {'> '}
            {l.text}
          </div>
        ))}
      </div>
    </div>
  );
}
